import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A horizontal stacked lollipop chart is a variation of the stacked bar chart, where
          horizontal lines are stacked with circular markers representing different subcategories.
          The total length of the line reflects the cumulative value, while the stacked markers
          highlight the individual contributions. This chart is ideal for showing the distribution
          of multiple components within each category in a visually clean and comparative way.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">Horizontal stacked lollipop charts are often utilized for:</p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Visualizing Component Contributions:</strong> They clearly show the proportional
            breakdown of each component within a total, such as the distribution of tasks across
            departments.
          </li>
          <li>
            <strong>Highlighting Cumulative Totals:</strong> The horizontal layout emphasizes
            overall values while displaying the relative size of individual components, such as
            total expenditures segmented by categories.
          </li>
          <li>
            <strong>Accommodating Long Category Labels:</strong> The horizontal format is ideal for
            categories with lengthy descriptions, ensuring readability without compromising visual
            clarity.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Horizontal stacked lollipop charts are useful for visualizing proportional data within
          categories, but they come with notable limitations:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficulty Comparing Subcategories Across Bars:</strong> The stacked format
            makes it challenging to directly compare individual subcategories between different
            bars, as their positions and lengths are not aligned.
          </li>
          <li>
            <strong>Cluttered Appearance with Many Subcategories:</strong> Including too many
            subcategories can overcrowd the chart, reducing its readability and making it harder for
            viewers to focus on critical data points.
          </li>
          <li>
            <strong>Limited Precision for Small Differences:</strong> Small variations in
            subcategory sizes may be hard to detect, which can obscure subtle but important trends
            or comparisons.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          For horizontal stacked lollipop charts, color selection plays a key role in distinguishing
          the different segments within each bar. Using distinct and contrasting colors helps make
          each segment easily identifiable, while gradients can show variations or trends.
          Thoughtful color choices enhance clarity, ensuring the chart remains readable and that
          important data points stand out.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative', 'sequential', 'diverging']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Horizontal stacked lollipop charts offer a visually appealing way to display data
          composition across categories, but they come with notable challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficult Subcategory Comparisons:</strong> The horizontal stacking makes it
            harder to compare subcategory values across different categories accurately.
          </li>
          <li>
            <strong>Clutter with Numerous Subcategories:</strong> Including too many subcategories
            can create visual clutter, reducing the chart’s clarity and effectiveness.
          </li>
          <li>
            <strong>Space Limitations:</strong> Horizontal layouts may require significant
            horizontal space, particularly when dealing with long category labels or numerous data
            points.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
